import { useEffect, useState } from 'react';
import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSse } from '@/hooks/useSse';
import { playOtpAlert } from '@/lib/sound';
import { startTitleFlash, stopTitleFlash } from '@/lib/title-flash';
import { cn } from '@/lib/utils';

type Source = 'BOOKING_COM' | 'EXPEDIA_TAAP' | 'AGODA' | 'UNKNOWN';

interface Alert {
  holdId: string;
  source: Source;
  externalRef: string;
  guestName: string | null;
  propertyName: string | null;
  amount: string | null;
  currency: string | null;
  receivedAt: number; // local timestamp
}

const SOURCE_LABEL: Record<Source, string> = {
  BOOKING_COM: 'Booking.com',
  EXPEDIA_TAAP: 'Expedia',
  AGODA: 'Agoda',
  UNKNOWN: 'OTA',
};

/**
 * Pop-up alert stack for `payment_action_required` SSE events. The user's
 * card was declined or there's a pay-now deadline — they need to act now or
 * the booking gets dropped by the OTA.
 *
 * Mounted globally in Layout. Shows top-right, plays the OTP alert sound,
 * flashes the title bar, persists until each alert is acknowledged.
 */
export function PaymentActionAlerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  useSse({
    onEvent: (ev) => {
      if (ev.type !== 'payment_action_required') return;
      const d = ev.data as Omit<Alert, 'receivedAt'>;
      setAlerts((prev) => {
        // Dedupe by holdId — if the same booking flares twice, replace, don't stack.
        const without = prev.filter((a) => a.holdId !== d.holdId);
        return [{ ...d, receivedAt: Date.now() }, ...without].slice(0, 10);
      });
      playOtpAlert();
      startTitleFlash('🚨 Card declined');
    },
  });

  // Stop flashing once the stack is empty.
  useEffect(() => {
    if (alerts.length === 0) stopTitleFlash();
  }, [alerts.length]);

  if (alerts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-16 z-50 flex flex-col items-center gap-2 px-4 sm:items-end sm:right-4 sm:left-auto">
      {alerts.map((a) => (
        <AlertCard key={a.holdId + a.receivedAt} alert={a} onDismiss={() => setAlerts((p) => p.filter((x) => x.holdId !== a.holdId))} />
      ))}
    </div>
  );
}

function AlertCard({ alert, onDismiss }: { alert: Alert; onDismiss: () => void }) {
  const portal = SOURCE_LABEL[alert.source];
  const subtitle =
    [alert.guestName, alert.propertyName, alert.amount ? `${alert.currency ?? ''} ${alert.amount}` : null]
      .filter(Boolean)
      .join(' · ') || 'Tap to act before the booking is dropped';

  return (
    <div
      role="alert"
      className={cn(
        'pointer-events-auto w-full max-w-md rounded-xl border-2 border-destructive bg-destructive/5 p-4 shadow-2xl backdrop-blur-sm',
        'animate-content-in',
      )}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
          <AlertTriangle className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-destructive">
              Card declined · {portal}
            </span>
            <span className="font-mono text-[10px] text-muted-foreground">#{alert.externalRef}</span>
          </div>
          <p className="mt-1 truncate text-sm font-medium">{subtitle}</p>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Update payment on the {portal} portal or cancel the booking before the OTA drops it.
          </p>
          <div className="mt-2 flex items-center gap-3 text-[11px]">
            <Link
              to="/holds"
              onClick={onDismiss}
              className="inline-flex items-center gap-1 font-medium text-destructive hover:underline"
            >
              Open Holds <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          aria-label="Acknowledge"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
